// Port of SuiteModel/DialogueBalance.swift: how prose divides between dialogue (words inside quotation marks) and
// narration. Double quotes are dialogue unless outermost quotations opened with ‘ outnumber them (British style); a quote
// left open runs to the end of its paragraph. Exact (given `plainParagraphs`, which the core ports exactly).
import { plainParagraphs } from '../proseMarkdown.ts';
import { curled, quotations } from './quotations.ts';
import { PROSE_WORD } from './proseWords.ts';
import { toScalars } from './scalars.ts';

export type QuoteStyle = 'double' | 'single';

export interface DialogueBalance {
  dialogueWords: number;
  narrationWords: number;
  /** Quoted passages (a line of dialogue, even if a tag interrupts it, counts twice). */
  lines: number;
}

export const emptyBalance = (): DialogueBalance => ({ dialogueWords: 0, narrationWords: 0, lines: 0 });
export const totalWords = (b: DialogueBalance) => b.dialogueWords + b.narrationWords;
export const dialogueShare = (b: DialogueBalance) => (totalWords(b) === 0 ? 0 : b.dialogueWords / totalWords(b));
export const wordsPerLine = (b: DialogueBalance) => (b.lines === 0 ? 0 : b.dialogueWords / b.lines);
export const addBalances = (a: DialogueBalance, b: DialogueBalance): DialogueBalance =>
  ({ dialogueWords: a.dialogueWords + b.dialogueWords, narrationWords: a.narrationWords + b.narrationWords, lines: a.lines + b.lines });

/** `DialogueBalance.style(of:)`: single if more outermost quotations open with ‘ than with “ (on curled text). */
export function quoteStyleOf(texts: readonly string[]): QuoteStyle {
  let doubles = 0, singles = 0;
  for (const text of texts) {
    for (const q of quotations(curled(toScalars(text)))) {
      if (q.depth !== 0) continue;
      if (q.double) doubles++; else singles++;
    }
  }
  return singles > doubles ? 'single' : 'double';
}

/** `DialogueBalance.measure`. */
export function measureDialogue(texts: readonly string[], style: QuoteStyle | null = null): DialogueBalance {
  const s = style ?? quoteStyleOf(texts);
  const result = emptyBalance();
  for (const text of texts) {
    for (const paragraph of plainParagraphs(text)) {
      const spans = quotedRanges(paragraph, s);
      result.lines += spans.length;
      for (const m of paragraph.matchAll(PROSE_WORD)) {
        const at = m.index!;
        if (spans.some(([a, b]) => at >= a && at < b)) result.dialogueWords++; else result.narrationWords++;
      }
    }
  }
  return result;
}

/** `DialogueBalance.quoted`: a paragraph's quoted ranges (UTF-16 [from, to)) in the manuscript's quote style. */
export function quotedRanges(paragraph: string, style: QuoteStyle): [number, number][] {
  const scalars = toScalars(paragraph);
  const ranges: [number, number][] = [];
  for (const q of quotations(curled(scalars))) {
    if (q.double !== (style === 'double')) continue;
    const range: [number, number] = [q.open, Math.max(q.open + 1, q.end)];
    // One nested inside another of the same kind is part of it.
    const last = ranges[ranges.length - 1];
    if (last && range[0] >= last[0] && range[0] < last[1]) continue;
    ranges.push(range);
  }
  if (!ranges.length) return [];
  const utf16 = [0];
  for (const c of scalars) utf16.push(utf16[utf16.length - 1] + c.length);
  return ranges.map(([a, b]) => [utf16[a], utf16[b]]);
}

/** `DialogueBalance`, named as the Swift names it. */
export const DialogueBalance = { style: quoteStyleOf, measure: measureDialogue, quoted: quotedRanges };
